import { useFlag } from '@calendly/feature-check-react'
import { useBooleanState } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from '@calendly/ui/components/dropdown-menu'
import { IconDotsVertical } from '@calendly/ui/components/glyphs/IconDotsVertical'
import { IconButton } from '@calendly/ui/components/icon-button'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useGetUserQuery } from 'common'
import { HostReschedule } from 'features/host_reschedule'
import { useGetMeetingIntelligenceUserSettingsQuery, useNotetakerEntitled } from 'features/meeting_intelligence'
import { DeleteRecapsModal } from 'features/tracked_meetings'
import { useFetchTrackedMeetingByForeignIdQuery } from 'hooks/tracked_meetings/api'
import { isCalendlyMeeting } from 'shared/utils/meeting_helpers'

import { CancelMeetingModal } from './cancel_meeting_modal'
import { MoreActionsMenu } from './more_actions_menu'
import { useRescheduleVisibility } from './use_reschedule_visibility'

/** HostReschedule renders outside DropdownMenu; register its onClick so the in-menu item can invoke it. */
const RescheduleRegistrationContext = createContext(null)

const RegisterRescheduleHandler = ({ onClick, rescheduleOnClickRef, onHandlerReadyChange }) => {
  useEffect(() => {
    rescheduleOnClickRef.current = onClick
    onHandlerReadyChange(true)

    return () => {
      rescheduleOnClickRef.current = null
      onHandlerReadyChange(false)
    }
  }, [onClick, onHandlerReadyChange, rescheduleOnClickRef])

  return null
}

const HostRescheduleMenuButton = ({ onClick }) => {
  const registration = useContext(RescheduleRegistrationContext)
  if (!registration) return null

  return (
    <RegisterRescheduleHandler
      onClick={onClick}
      rescheduleOnClickRef={registration.rescheduleOnClickRef}
      onHandlerReadyChange={registration.onHandlerReadyChange}
    />
  )
}

export const MoreActions = ({ event,
  inProgress = false,
  showReportModal, clearSelectedEvent, updateSelectedEvent, sendAnalyticsForReschedule, showCancelInRow = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useBooleanState(false)
  const [isDeleteRecapModalOpen, openDeleteRecapModal, closeDeleteRecapModal] = useBooleanState(false)
  const rescheduleOnClickRef = useRef(null)
  const [isRescheduleHandlerReady, setIsRescheduleHandlerReady] = useState(false)
  const { data: currentUser } = useGetUserQuery()
  const isNotPretending = currentUser && !currentUser.is_pretending

  const { isEntitled: isNotetakerEntitled } = useNotetakerEntitled()
  const { data: meetingIntelligence } = useFetchTrackedMeetingByForeignIdQuery(
    event?.uuid,
    { skip: !event || !isNotetakerEntitled }
  )
  const { data: meetingIntelligenceSettings } = useGetMeetingIntelligenceUserSettingsQuery({})

  const shouldShowRemoveNotetakerItem = isNotPretending
    && !!meetingIntelligenceSettings?.miEnabled
    && meetingIntelligence?.enabled
    && ['Scheduled'].includes(meetingIntelligence?.status)
  /* Show Cancel in menu when it's not in the row (overflow or phone+notetaker). */
  const shouldShowCancelItem = event.policy?.cancel && isNotPretending && !showCancelInRow
  const shouldShowDeleteItem = Boolean(meetingIntelligence?.trackedMeetingUuid)
    && meetingIntelligence?.status === 'Completed'
    && !meetingIntelligence?.deleted
  /*
    trust_and_safety_reactive_block only gates the Report item (hidden when the flag is on).
    It does not swap MoreActions or hide the kebab; the ⋮ still renders when any other
    menu item is eligible (cancel-in-menu, reschedule-in-dropdown, notetaker, etc.).
  */
  const featureFlagEnabled = useFlag('trust_and_safety_reactive_block')
  const shouldShowReportItem = !event?.past && isCalendlyMeeting(event) && !featureFlagEnabled
  const { showRescheduleInDropdown: shouldShowRescheduleItem } = useRescheduleVisibility(event, inProgress)
  const shouldShowRescheduleInMenu = shouldShowRescheduleItem && isRescheduleHandlerReady

  const shouldShowMenu = [
    shouldShowRemoveNotetakerItem,
    shouldShowCancelItem,
    shouldShowDeleteItem,
    shouldShowReportItem,
    shouldShowRescheduleItem,
  ].some(Boolean)

  const handleToggleDropdown = () => {
    setIsDropdownOpen((open) => !open)
  }

  const closeDropdown = () => {
    setIsDropdownOpen(false)
  }

  const rescheduleRegistration = useMemo(() => ({
    rescheduleOnClickRef,
    onHandlerReadyChange: setIsRescheduleHandlerReady,
  }), [])

  if (!shouldShowMenu) return null

  return (
    <>
      <DropdownMenu isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
        <DropdownMenuTrigger>
          <IconButton
            aria-label={I18n.t('publisher.meetings.actions.more_actions')}
            onClick={handleToggleDropdown}
            aria-expanded={isDropdownOpen}
            aria-haspopup="menu"
          >
            <UiIcon icon={IconDotsVertical} size="small" title="" />
          </IconButton>
        </DropdownMenuTrigger>
        <MoreActionsMenu
          event={event}
          isDropdownOpen={isDropdownOpen}
          meetingIntelligence={meetingIntelligence}
          openCancelModal={openCancelModal}
          openDeleteRecapModal={openDeleteRecapModal}
          shouldShowCancelItem={shouldShowCancelItem}
          shouldShowDeleteItem={shouldShowDeleteItem}
          shouldShowMenu={shouldShowMenu}
          shouldShowRemoveNotetakerItem={shouldShowRemoveNotetakerItem}
          shouldShowReportItem={shouldShowReportItem}
          shouldShowRescheduleItem={shouldShowRescheduleInMenu}
          rescheduleOnClickRef={rescheduleOnClickRef}
          sendAnalyticsForReschedule={sendAnalyticsForReschedule}
          showReportModal={showReportModal}
          closeDropdown={closeDropdown}
        />
      </DropdownMenu>
      {shouldShowRescheduleItem && (
        <RescheduleRegistrationContext.Provider value={rescheduleRegistration}>
          <HostReschedule
            event={event}
            invitee={event.invitee}
            Button={HostRescheduleMenuButton}
            sendAnalytics={sendAnalyticsForReschedule}
          />
        </RescheduleRegistrationContext.Provider>
      )}
      {isCancelModalOpen && (
        <CancelMeetingModal
          event={event}
          onClose={closeCancelModal}
          clearSelectedEvent={clearSelectedEvent}
          updateSelectedEvent={updateSelectedEvent}
          showReportModal={showReportModal}
        />
      )}
      {shouldShowDeleteItem && (
        <DeleteRecapsModal
          isOpen={isDeleteRecapModalOpen}
          meetingUuids={[meetingIntelligence.trackedMeetingUuid]}
          onClose={closeDeleteRecapModal}
          onSuccess={closeDeleteRecapModal}
        />
      )}
    </>
  )
}
